import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Stack, useMediaQuery, useTheme, ClickAwayListener, Portal, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import DropdownArrowNavIcon from '../icons/DropdownArrowNavIcon';

// Mobile-First Styled Components
const SelectorContainer = styled(Stack)(({ theme }) => ({
  gap: '8px',
  position: 'relative',
  zIndex: 100,
  [theme.breakpoints.up('sm')]: {
    gap: '12px',
  },
  [theme.breakpoints.up('md')]: {
    gap: '16px',
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  [theme.breakpoints.up('md')]: {
    fontSize: '10px',
    fontWeight: 400,
    color: '#EF232A',
    textTransform: 'none',
    letterSpacing: 'normal',
  },
}));

const DropdownContainer = styled(Box)(({ theme }) => ({
  borderRadius: '10px',
  border: '1px solid #E2E8F0',
  padding: '14px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  backgroundColor: '#ffffff',
  width: '100%',
  minHeight: '48px',
  transition: 'all 0.2s ease',
  position: 'relative',
  zIndex: 101,
  '&:hover': {
    borderColor: '#0B91D6',
    backgroundColor: '#F8FAFC',
  },
  '&:active': {
    backgroundColor: '#F1F5F9',
  },
  [theme.breakpoints.up('sm')]: {
    padding: '14px 18px',
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: '8px',
    border: '0.2px solid #A0A0AA',
    padding: '16px',
    minWidth: '540px',
    width: 'auto',
  },
}));

const DropdownText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#0B91D6',
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
  },
}));

const DropdownItem = styled(Box)(({ theme, selected }) => ({
  padding: '14px 16px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: selected ? '#F0F9FF' : 'transparent',
  borderBottom: '1px solid #F1F5F9',
  transition: 'background-color 0.15s ease',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: '#F8FAFC',
  },
  '&:active': {
    backgroundColor: '#F1F5F9',
  },
  [theme.breakpoints.up('md')]: {
    padding: '12px 16px',
    borderBottom: 'none',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
  },
}));

const SelectedIndicator = styled(Box)(({ theme }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#0B91D6',
}));

const MapGridSelector = ({ value, onChange, isMobile, isTablet }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });

  const gridOptions = [
    { label: '25  pins 5*5', description: '5x5 Grid (25 pins)' },
    { label: '16  pins 4*4', description: '4x4 Grid (16 pins)' },
    { label: '36  pins 6*6', description: '6x6 Grid (36 pins)' },
    { label: '49  pins 7*7', description: '7x7 Grid (49 pins)' },
    { label: '64  pins 8*8', description: '8x8 Grid (64 pins)' },
    { label: '81  pins 9*9', description: '9x9 Grid (81 pins)' },
  ];

  // Calculate menu position when opening
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleClickAway = (event) => {
    // Check if click is inside the dropdown menu
    if (event.target.closest('[data-grid-dropdown-menu]')) {
      return;
    }
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Format display text for mobile
  const getDisplayText = () => {
    if (!value) return 'Select grid size';
    if (isSmallScreen) {
      // Show shorter version on mobile
      const option = gridOptions.find(o => o.label === value);
      return option ? option.description : value;
    }
    return value;
  };

  return (
    <SelectorContainer>
      <FieldLabel>Map Grid*</FieldLabel>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: 'relative' }} ref={containerRef}>
          <DropdownContainer onClick={handleToggle}>
            <DropdownText>{getDisplayText()}</DropdownText>
            <Box
              sx={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.2s ease',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <DropdownArrowNavIcon width={10} height={6} />
            </Box>
          </DropdownContainer>

          {/* Use Portal to render dropdown outside of parent containers */}
          {isOpen && (
            <Portal>
              <Paper
                data-grid-dropdown-menu
                elevation={8}
                sx={{
                  position: 'fixed',
                  top: menuPosition.top,
                  left: menuPosition.left,
                  width: menuPosition.width,
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.25)',
                  zIndex: 9999,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  border: '1px solid #E2E8F0',
                }}
              >
                {gridOptions.map((option) => (
                  <DropdownItem
                    key={option.label}
                    selected={value === option.label}
                    onClick={() => handleSelect(option.label)}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '15px',
                          fontWeight: value === option.label ? 600 : 400,
                          color: value === option.label ? '#0B91D6' : '#121927',
                        }}
                      >
                        {option.description}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#64748b',
                          mt: 0.25,
                        }}
                      >
                        {option.label}
                      </Typography>
                    </Box>
                    {value === option.label && <SelectedIndicator />}
                  </DropdownItem>
                ))}
              </Paper>
            </Portal>
          )}
        </Box>
      </ClickAwayListener>
    </SelectorContainer>
  );
};

export default MapGridSelector;
